/**
 * «Юниверситет» (28.09): подписанные ссылки на медиа пакета — GET /api/uni/:pack/media.
 * Теги <video>/<audio>/<img> не шлют Authorization, а скачивание в blob ломает перемотку
 * (ТЗ), поэтому сервер выдаёт короткоживущие подписи (~6 ч), а мы обновляем их примерно
 * за 10 минут до срока. Ссылка — относительная (/api/uni-media/…), дописываем API_BASE:
 * на staging это same-origin домен Pages (workers.dev у части провайдеров заблокирован).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../api/client";
import type { MediaUrl } from "./ctx";

/* Как в api/client.ts (там константа не экспортируется) и eo/MediaBackup.tsx. */
const API_BASE: string = ((import.meta.env?.VITE_API_URL as string | undefined) ?? "").replace(/\/$/, "");

interface MediaResp {
  base: string;
  /** unix-секунды, до которых действуют подписи */
  expires: number;
  /** rel → «?v=<sha8>&e=<unix>&t=<hex64>» */
  files: Record<string, string>;
}

const EARLY_S = 600; // обновляем за 10 минут до истечения
const MIN_WAIT_MS = 5 * 60_000; // не чаще раза в 5 минут, даже если часы устройства врут
const RETRY_MS = 30_000;

export function useUniMedia(packId: string): MediaUrl {
  const [cur, setCur] = useState<{ pack: string; m: MediaResp } | null>(null);
  const [tick, setTick] = useState(0);
  const expRef = useRef(0);

  useEffect(() => {
    let alive = true;
    let timer = 0;
    api<MediaResp>(`/api/uni/${encodeURIComponent(packId)}/media`)
      .then((m) => {
        if (!alive) return;
        setCur({ pack: packId, m });
        expRef.current = m.expires;
        const wait = (m.expires - EARLY_S) * 1000 - Date.now();
        timer = window.setTimeout(() => setTick((t) => t + 1), Math.max(MIN_WAIT_MS, wait));
      })
      .catch(() => {
        if (alive) timer = window.setTimeout(() => setTick((t) => t + 1), RETRY_MS);
      });
    return () => { alive = false; window.clearTimeout(timer); };
  }, [packId, tick]);

  /* Таймеры в фоне Telegram засыпают: вернулись на экран — проверяем срок сами. */
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (expRef.current && Date.now() / 1000 > expRef.current - EARLY_S) setTick((t) => t + 1);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const m = cur && cur.pack === packId ? cur.m : null;
  return useCallback<MediaUrl>((rel) => {
    if (!rel || !m) return undefined;
    const q = m.files[rel];
    return q === undefined ? undefined : `${API_BASE}${m.base}${rel}${q}`;
  }, [m]);
}
